import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { helpCircleOutline } from "ionicons/icons";
import { z } from "zod";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { BreakDurationField } from "@/components/molecules/shift/break-duration-field";
import { durationToMinutes, parseTimeString } from "@/utils/time";

interface HoursFormModalProps {
  modalRef: React.RefObject<HTMLIonModalElement>;
  hoursInfoModalRef: React.RefObject<HTMLIonModalElement>;
  formHours: any;
  FormElementHours: any;
  validator: any;
  closeModal: () => void;
}

export function HoursFormModal({
  modalRef,
  hoursInfoModalRef,
  formHours,
  FormElementHours,
  validator,
  closeModal,
}: HoursFormModalProps) {
  const { t } = useTranslation();

  // Validation function to check if break duration is less than total shift duration
  const validateBreakDuration = (val: string): boolean => {
    const startTime = formHours.getFieldValue("startTime");
    const endTime = formHours.getFieldValue("endTime");

    if (!startTime || !endTime || !val) {
      return true;
    }

    const breakDuration = durationToMinutes(parseTimeString(val));
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle overnight shifts (when end time is on the next day)
    // If end time is earlier than or equal to start time, add 24 hours (1440 minutes)
    if (endMinutes <= startMinutes) {
      endMinutes += 1440;
    }

    const totalMinutes = endMinutes - startMinutes;

    return breakDuration < totalMinutes;
  };

  return (
    <ModalSheetAutoHeight ref={modalRef} onDidDismiss={closeModal}>
      <FormElementHours>
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">{t("shift:hours.modal.title")}</h2>
            <button
              type="button"
              onClick={() => hoursInfoModalRef.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Form fields */}
          <formHours.AppField
            name="startTime"
            validators={{
              onBlur: validator.time({
                name: "startTime",
                schema: validator.required({ name: "startTime" }),
              }),
              onSubmit: validator.time({
                name: "startTime",
                schema: validator.required({ name: "startTime" }),
              }),
            }}
            children={(field: any) => (
              <div className="flex flex-col gap-2">
                <field.InputField
                  type="time"
                  className="time-input"
                  required
                  labelPlacement="start"
                  data-force-24h="true"
                  withAsterisk={false}
                />
              </div>
            )}
          />

          <formHours.AppField
            name="endTime"
            validators={{
              onBlur: validator.time({
                name: "endTime",
                schema: validator.required({ name: "endTime" }),
              }),
              onSubmit: validator.time({
                name: "endTime",
                schema: validator.required({ name: "endTime" }),
              }),
            }}
            children={(field: any) => (
              <div className="flex flex-col gap-2">
                <field.InputField
                  type="time"
                  className="time-input"
                  required
                  labelPlacement="start"
                  data-force-24h="true"
                  withAsterisk={false}
                />
              </div>
            )}
          />

          <formHours.AppField
            name="breakDuration"
            validators={{
              onBlur: validator
                .time({
                  name: "breakDuration",
                  schema: validator.required({ name: "breakDuration" }),
                })
                .refine(validateBreakDuration, {
                  message: t("shift:hours.validation.breakTooLong"),
                }),
              onSubmit: validator
                .time({
                  name: "breakDuration",
                  schema: validator.required({ name: "breakDuration" }),
                })
                .refine(validateBreakDuration, {
                  message: t("shift:hours.validation.breakTooLong"),
                }),
            }}
            children={(field: any) => {
              // Get break time in minutes for warnings
              const breakDuration = field.state.value
                ? durationToMinutes(parseTimeString(field.state.value))
                : 0;
              const startTime = formHours.getFieldValue("startTime");
              const endTime = formHours.getFieldValue("endTime");

              // Show warning if break is more than 2 hours (120 minutes)
              const showWarning = !!(breakDuration > 120 && startTime && endTime);

              return <BreakDurationField field={field} showWarning={showWarning} />;
            }}
          />

          {/* Optional free-text remark on the employee's own hours line (issue #426) */}
          <formHours.AppField
            name="comment"
            children={(field: any) => (
              <div className="flex flex-col gap-2">
                <field.TextareaField
                  name="comment"
                  data-testid="hours-comment"
                  autoGrow
                  rows={3}
                  maxlength={1000}
                  labelPlacement="stacked"
                  withAsterisk={false}
                  withPlaceholder
                  label={t("shift:hours.modal.commentLabel")}
                  placeholder={t("shift:hours.modal.commentPlaceholder")}
                />
              </div>
            )}
          />

          {/* Submit button */}
          <formHours.Subscribe
            selector={(state: any) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]: [boolean, boolean]) => (
              <button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full rounded-lg border border-solid border-bordercolor-primary bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:opacity-50"
              >
                {isSubmitting ? t("common:loading") : t("shift:hours.modal.save")}
              </button>
            )}
          />
        </div>
      </FormElementHours>
    </ModalSheetAutoHeight>
  );
}
